import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { COUNT_TIPS } from '../lib/adminCounts';
import { fetchAllRows } from '../../lib/fetchAll';
import { fetchAllIn } from '../lib/adminCounts';
import { formatRating } from '../../lib/ratings';

import { onCoverError } from '../../lib/coverFallback';
import { logAudit } from '../lib/auditLog';
function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// Same shape as BooksSection.jsx's own Pill -- kept as a local copy rather
// than a shared import since these admin sections don't currently share a
// components module, and this is the first place this file has needed more
// than StatusPill's fixed three states.
function Pill({ children, color = 'gray' }) {
  const styles = {
    gray:  { background: 'var(--brand-cream)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    green: { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' },
    amber: { background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
    blue:  { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 'var(--font-size-xxs)',
      fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--radius-xxl)',
      letterSpacing: 0.2, whiteSpace: 'nowrap', flexShrink: 0,
      ...styles[color],
    }}>
      {children}
    </span>
  );
}

function StatusPill({ status, verified }) {
  if (status === 'suspended') return (
    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, padding: '4px 12px', borderRadius: 'var(--radius-xxl)', background: 'var(--feedback-error-bg)', color: '#991b1b', border: '1px solid #fca5a5', whiteSpace: 'nowrap' }}>Suspended</span>
  );
  if (verified) return (
    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, padding: '4px 12px', borderRadius: 'var(--radius-xxl)', background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', whiteSpace: 'nowrap' }}>✓ Verified</span>
  );
  return (
    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, padding: '4px 12px', borderRadius: 'var(--radius-xxl)', background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0', whiteSpace: 'nowrap' }}>Active</span>
  );
}

// ── Tooltip ⓘ ─────────────────────────────────────────────────────────────────
function InfoTip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', cursor: 'help', userSelect: 'none' }}
      >ⓘ</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)',
          background: 'var(--neutral-800)', color: 'var(--brand-white)', fontSize: 'var(--font-size-xs)', lineHeight: 1.5,
          padding: '6px 10px', borderRadius: 'var(--radius-md)', whiteSpace: 'pre-wrap',
          width: 220, zIndex: 999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
        }}>{text}</span>
      )}
    </span>
  );
}

// ── Stats bar ─────────────────────────────────────────────────────────────────
function StatsBar({ total, active, suspended, isMobile }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? 10 : 12, marginBottom: 'var(--space-14)',
    }}>
      {[
        { label: 'Catalogue Authors', value: total,     accent: '#d97706', tip: COUNT_TIPS.catalogueAuthors },
        { label: 'Author Accounts',   value: active,    accent: '#16a34a', tip: COUNT_TIPS.portalActive },
        { label: 'Suspended',         value: suspended, accent: '#dc2626', tip: 'Author accounts an admin has suspended. They cannot sign in.\n\nReader accounts are suspended separately, on the Users page.' },
      ].map(c => (
        <div key={c.label} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderTop: `3px solid ${c.accent}`, borderRadius: 'var(--radius-lg)', padding: isMobile ? '12px 14px' : '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 'var(--space-sm)' }}>
            <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{c.label}</span>
            <InfoTip text={c.tip} />
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 28, fontWeight: 700, color: 'var(--neutral-800)', lineHeight: 1 }}>{c.value ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

// ── Portal metrics bar ────────────────────────────────────────────────────────
function PortalMetricsBar({ metrics, accountCount, isMobile }) {
  const cards = [
    {
      label:   'Claimed Catalogue',
      value:   metrics.withPortalLogin,
      accent:  '#2D4A3E',
      bg:      '#f0fdf4',
      icon:    '✅',
      tip:     COUNT_TIPS.claimedCatalogue,
    },
    {
      label:   'Catalog Only',
      value:   metrics.catalogOnly,
      accent:  '#d97706',
      bg:      '#fffbeb',
      icon:    '📋',
      tip:     COUNT_TIPS.catalogueOnly,
    },
    {
      label:   'Books Pending',
      value:   metrics.pendingBooks,
      accent:  '#f59e0b',
      bg:      '#fef9ec',
      icon:    '⏳',
      tip:     'Books submitted through the author portal (submission_type = "author") and still awaiting admin review.\n\nBooks added by readers or CSV are NOT counted here, see the Books page for those.',
    },
    {
      label:   'Books Live',
      value:   metrics.liveBooks,
      accent:  '#16a34a',
      bg:      '#f0fdf4',
      icon:    '📚',
      tip:     'Books submitted through the author portal that are approved and live.\n\nA subset of the Books page total, which also counts reader and CSV submissions.',
    },
  ];

  return (
    <div style={{ marginBottom: 'var(--space-xxl)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)' }}>
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Author Portal Metrics
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? 10 : 12,
      }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: c.bg, border: `1px solid ${c.accent}22`, borderLeft: `3px solid ${c.accent}`, borderRadius: 'var(--radius-lg)', padding: isMobile ? '12px 14px' : '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 'var(--space-sm)' }}>
              <span style={{ fontSize: 'var(--font-size-md)' }}>{c.icon}</span>
              <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{c.label}</span>
              <InfoTip text={c.tip} />
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 26, fontWeight: 700, color: 'var(--neutral-800)', lineHeight: 1 }}>
              {metrics.loading ? '—' : (c.value ?? 0)}
            </div>
          </div>
        ))}
      </div>

    
    </div>
  );
}

// ── Author Detail Modal ───────────────────────────────────────────────────────
// One suspend/restore path for the detail panel and both list views. The two
// list buttons used to write the audit line and move the row on screen without
// looking at the save, so a refused update showed the author as suspended while
// the database still had them active. Returns the new status, or null if the
// admin cancelled or the save failed (the admin is told why).
async function toggleAuthorSuspend(author) {
  const isSusp    = author.status === 'suspended';
  const newStatus = isSusp ? 'active' : 'suspended';
  if (!confirm(`${isSusp ? 'Unsuspend' : 'Suspend'} ${author.name}?`)) return null;
  const { data: rows, error } = await supabase
    .from('author_profiles')
    .update({ status: newStatus, updated_at: new Date().toISOString() })
    .eq('id', author.id)
    .select('id');
  if (error || !rows?.length) {
    alert(`Could not ${isSusp ? 'unsuspend' : 'suspend'} ${author.name}: ${error?.message || 'the update changed no row (blocked by a policy, or the profile no longer exists).'} Nothing was changed.`);
    return null;
  }
  await logAudit('update', 'author', `${isSusp ? 'Unsuspended' : 'Suspended'} author ${author.name}`, author.id);
  return newStatus;
}

function AuthorDetailModal({ author, onClose, onStatusChange }) {
  const [books,   setBooks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting,  setActing]  = useState(false);
  // PR 3 (admin claim view and un-claim), 1.9: "Admin author view: primary
  // name, aliases, confirmed status, read-only." author_names is PR 1's
  // table (reanlo-author 20260916000000) -- reading it here needs its own
  // admin-read RLS policy (20260920000000), since PR 1 only let an author
  // read their own rows.
  const [names,        setNames]        = useState([]);
  const [namesLoading, setNamesLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const query = supabase
        .from('books')
        .select('id, title, cover_image_url, status, created_at, author_name, submission_type')
        .order('created_at', { ascending: false })
        .limit(30);
      if (author.authors_table_id) {
        query.eq('author_id', author.authors_table_id);
      } else {
        query.ilike('author_name', `%${author.name}%`);
      }
      const { data } = await query;
      setBooks(data || []);
      setLoading(false);
    }
    load();
  }, [author.id]);

  useEffect(() => {
    async function loadNames() {
      if (!author.authors_table_id) { setNames([]); setNamesLoading(false); return; }
      const { data, error } = await supabase
        .from('author_names')
        .select('id, raw_name, is_primary, confirmed_at')
        .eq('author_id', author.authors_table_id)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true });
      if (error) { console.error('author_names fetch error:', error.message); }
      setNames(data || []);
      setNamesLoading(false);
    }
    loadNames();
  }, [author.authors_table_id]);

  async function handleSuspend() {
    setActing(true);
    const newStatus = await toggleAuthorSuspend(author);
    if (newStatus) onStatusChange(author.id, newStatus);
    setActing(false);
  }

  const isSuspended  = author.status === 'suspended';
  const portalBooks  = books.filter(b => b.submission_type === 'author');
  const pendingCount = portalBooks.filter(b => b.status === 'pending').length;
  const liveCount    = portalBooks.filter(b => b.status === 'live').length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-lg)' }}>
      <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: 660, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--brand-sand)', display: 'flex', gap: 'var(--space-14)', alignItems: 'flex-start' }}>
          <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-circle)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0, border: '2px solid var(--brand-sand)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
            {author.avatar_url ? <img src={author.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '✍️'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-xs)', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 'var(--space-none)', fontSize: 17, fontWeight: 800, color: 'var(--neutral-800)' }}>{author.name}</h2>
              <StatusPill status={author.status} verified={author.is_verified} />
              {author.hasPortalAccount && (
                <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '3px 8px', borderRadius: 'var(--radius-md)', background: 'var(--feedback-success-bg)', color: '#16a34a', border: '1px solid #bbf7d0' }}>✅ Portal Login</span>
              )}
            </div>
            {author.handle   && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', marginBottom: 'var(--space-xxs)' }}>@{author.handle}</div>}
            {author.location && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', marginBottom: 'var(--space-xxs)' }}>📍 {author.location}</div>}
            {author.email    && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', marginBottom: 'var(--space-xxs)' }}>✉️ {author.email}</div>}
            {author.bio      && <p style={{ margin: '6px 0 0', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>{author.bio.slice(0, 220)}{author.bio.length > 220 ? '…' : ''}</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xxl)', cursor: 'pointer', color: 'var(--neutral-400)', padding: 'var(--space-xs)', lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-none)', borderBottom: '1px solid var(--brand-sand)' }}>
          {[
            { label: 'Total Books',    value: author.books_count   ?? 0, tip: 'Every book in the catalogue credited to this author, however it was added.' },
            { label: 'Portal Pending', value: pendingCount,              tip: 'This author\u2019s own portal submissions still awaiting review.' },
            { label: 'Portal Live',    value: liveCount,                 tip: 'This author\u2019s own portal submissions that are approved and live.' },
            { label: 'Readers',        value: author.readers_count ?? 0, tip: 'Readers with at least one of this author\u2019s books saved to a shelf. Counts readers, not saves.' },
            // The tip has always said unrated books are left out of the average.
            // The value then printed 0.0 when there were no rated books at all \u2014
            // breaking its own stated rule in exactly the case the rule is for.
            { label: 'Avg Rating',     value: formatRating(author.avg_rating, { treatZeroAsUnknown: true }), tip: 'Mean rating across this author\u2019s rated books. Books with no rating are left out of the average entirely, and an author with no rated books shows a dash rather than a zero.' },
          ].map((s, i) => (
            <div key={s.label} style={{ flex: 1, padding: '12px 8px', borderRight: i < 4 ? '1px solid var(--brand-sand)' : 'none', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--neutral-800)', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: 'var(--neutral-400)', marginTop: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                <span>{s.label}</span><InfoTip text={s.tip} width={200} />
              </div>
            </div>
          ))}
        </div>

        {author.social_links?.length > 0 && (
          <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--brand-sand)', display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
            {author.social_links.map((s, i) => (
              <span key={i} style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '3px 10px', background: 'var(--brand-cream)', color: 'var(--neutral-500)', borderRadius: 'var(--radius-10)' }}>
                {s.platform}: @{s.handle}
              </span>
            ))}
          </div>
        )}

        {/* PR 3, 1.9: read-only -- no edit/confirm controls here on purpose.
            Light verification of authorship and two-author conflict
            resolution are both explicitly V2 in the spec, not this. */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--brand-sand)' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Names
          </h3>
          {namesLoading ? (
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: 0 }}>Loading…</p>
          ) : names.length === 0 ? (
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: 0 }}>No name on file.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {names.map(n => (
                <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--font-size-base)', fontWeight: n.is_primary ? 700 : 500, color: 'var(--neutral-800)' }}>{n.raw_name}</span>
                  {n.is_primary && <Pill color="blue">primary</Pill>}
                  {n.confirmed_at ? (
                    <Pill color="green">confirmed {new Date(n.confirmed_at).toLocaleDateString()}</Pill>
                  ) : (
                    <Pill color="amber">unconfirmed</Pill>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: '14px 20px' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Books ({loading ? '…' : books.length})
          </h3>
          {loading ? (
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', padding: '20px 0', textAlign: 'center' }}>Loading…</p>
          ) : books.length === 0 ? (
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', fontStyle: 'italic' }}>No books found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', maxHeight: 260, overflowY: 'auto' }}>
              {books.map(bk => (
                <div key={bk.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '8px 10px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)' }}>
                  <div style={{ width: 28, height: 40, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--brand-sand)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {bk.cover_image_url ? <img src={bk.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} /> : <span style={{ fontSize: 'var(--font-size-xs)' }}>📖</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bk.title}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>{new Date(bk.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    {bk.submission_type === 'author' && (
                      <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)' }}>PORTAL</span>
                    )}
                    <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-10)', flexShrink: 0, background: bk.status === 'live' ? '#d1fae5' : bk.status === 'pending' ? 'var(--feedback-warning-bg)' : 'var(--feedback-error-bg)', color: bk.status === 'live' ? '#065f46' : bk.status === 'pending' ? 'var(--feedback-warning-ink)' : '#991b1b' }}>{bk.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--brand-sand)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <button onClick={handleSuspend} disabled={acting} style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', cursor: acting ? 'not-allowed' : 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 600, opacity: acting ? 0.6 : 1, background: isSuspended ? '#d1fae5' : 'var(--feedback-error-bg)', color: isSuspended ? '#065f46' : 'var(--feedback-error)' }}>
              {acting ? '…' : isSuspended ? '✓ Unsuspend' : '⊘ Suspend'}
            </button>
          </div>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', color: 'var(--neutral-800)' }}>Close</button>
        </div>
      </div>

      {/* Two accounts claiming one catalogue author is the only way these can
          disagree, and it is worth seeing rather than rounding away: the
          author's books are split across two records. */}
      {!metrics.loading && accountCount > metrics.withPortalLogin && (
        <div style={{
          marginTop: 10, padding: '9px 12px', borderRadius: 9,
          background: '#fffbeb', border: '1px solid #fde68a',
          fontSize: 12, color: '#92400e', lineHeight: 1.5,
        }}>
          <b>{accountCount - metrics.withPortalLogin}</b> duplicate claim
          {accountCount - metrics.withPortalLogin === 1 ? '' : 's'}:
          {' '}{accountCount} author accounts are pointing at only
          {' '}{metrics.withPortalLogin} distinct catalogue authors, so at least two
          accounts share one. Their books are split across both records.
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN SECTION
// ══════════════════════════════════════════════════════════════════════════════
export default function AdminAuthorSection({ onBadgeChange, isMobile }) {
  const [authors,       setAuthors]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [filter,        setFilter]        = useState('all');
  const [selected,      setSelected]      = useState(null);
  const [stats,         setStats]         = useState({ total: 0, active: 0, suspended: 0 });
  const [portalMetrics, setPortalMetrics] = useState({ loading: true, withPortalLogin: 0, catalogOnly: 0, pendingBooks: 0, liveBooks: 0 });
  const loadPortalMetrics = useCallback(async () => {
    setPortalMetrics(m => ({ ...m, loading: true }));
    try {
      // 1. Total in authors catalog (source of truth)
      const { count: totalInCatalog } = await supabase
        .from('authors')
        .select('id', { count: 'exact', head: true });

      // 2. Distinct claimed authors (use DISTINCT to avoid counting duplicate author_profiles rows)
      const claimedRows = await fetchAllRows(() => supabase
        .from('author_profiles')
        .select('id, authors_table_id')
        .not('authors_table_id', 'is', null));

      const distinctClaimedCount = new Set(
        (claimedRows || []).map(r => r.authors_table_id).filter(Boolean)
      ).size;

      // withPortalLogin = unique authors who have a portal account (deduped)
      const withPortalLogin = distinctClaimedCount;
      // catalogOnly = authors in catalog with no portal account at all
      const catalogOnly = Math.max(0, (totalInCatalog ?? 0) - distinctClaimedCount);

      // 3. Books pending from author portal
      const { count: pendingBooks } = await supabase
        .from('books')
        .select('id', { count: 'exact', head: true })
        .eq('submission_type', 'author')
        .eq('status', 'pending');

      // 4. Books live from author portal
      const { count: liveBooks } = await supabase
        .from('books')
        .select('id', { count: 'exact', head: true })
        .eq('submission_type', 'author')
        .eq('status', 'live');

      setPortalMetrics({
        loading:        false,
        withPortalLogin: withPortalLogin ?? 0,
        catalogOnly,
        pendingBooks:   pendingBooks ?? 0,
        liveBooks:      liveBooks    ?? 0,
      });
    } catch (err) {
      console.error('Portal metrics error:', err.message);
      setPortalMetrics(m => ({ ...m, loading: false }));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Paged: stats.total / stats.active are counted off this array, so a
      // bare select would cap the numbers at 1,000 as well as the list.
      const rows = await fetchAllRows(() => supabase
        .from('author_profiles')
        .select('id, user_id, name, handle, location, bio, avatar_url, social_links, is_verified, books_count, readers_count, avg_rating, created_at, updated_at, authors_table_id, status'));
      rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const error = null;

      if (error) { console.error('AdminAuthorSection fetch error:', error.message); setLoading(false); return; }

      const allAuthors = await fetchAllRows(() => supabase
        .from('authors')
        .select('id, email, display_name, bio, profile_photo_url, created_at'));

      const claimedIds = new Set((rows || []).map(r => r.authors_table_id).filter(Boolean));
      const unclaimedAuthors = (allAuthors || []).filter(a => !claimedIds.has(a.id));

      const allAuthorTableIds = (allAuthors || []).map(a => a.id);
      const bookCountMap = {};
      if (allAuthorTableIds.length > 0) {
        const bookCounts = await fetchAllIn('books', 'id, author_id', 'author_id', allAuthorTableIds);
        (bookCounts || []).forEach(b => {
          if (b.author_id) bookCountMap[b.author_id] = (bookCountMap[b.author_id] || 0) + 1;
        });
      }

      const unclaimedIds = unclaimedAuthors.map(a => a.id);
      const readerCountMap = {};
      if (unclaimedIds.length > 0) {
        const unclaimedBooks = await fetchAllIn('books', 'id, author_id', 'author_id', unclaimedIds);
        const bookIds = (unclaimedBooks || []).map(b => b.id);
        const bookToAuthor = {};
        (unclaimedBooks || []).forEach(b => { bookToAuthor[b.id] = b.author_id; });
        if (bookIds.length > 0) {
          const libraryRows = await fetchAllIn('user_library', 'id, book_id, user_id', 'book_id', bookIds);
          const authorReaders = {};
          (libraryRows || []).forEach(r => {
            const authorId = bookToAuthor[r.book_id];
            if (!authorId) return;
            if (!authorReaders[authorId]) authorReaders[authorId] = new Set();
            authorReaders[authorId].add(r.user_id);
          });
          Object.entries(authorReaders).forEach(([authorId, readers]) => {
            readerCountMap[authorId] = readers.size;
          });
        }
      }

      const authorEmailMap = {};
      (allAuthors || []).forEach(a => { authorEmailMap[a.id] = a.email; });

      const claimedUserIds = (rows || []).map(r => r.user_id).filter(Boolean);
      const readerSet = new Set();
      if (claimedUserIds.length > 0) {
        const readerProfiles = await fetchAllIn('user_profiles', 'id, user_id', 'user_id', claimedUserIds);
        (readerProfiles || []).forEach(r => readerSet.add(r.user_id));
      }

      const enrichedClaimed = (rows || []).map(r => ({
        ...r,
        status:           r.status || 'active',
        email:            authorEmailMap[r.authors_table_id] || null,
        books_count:      bookCountMap[r.authors_table_id] || 0,
        hasPortalAccount: true,
        hasReaderAccount: readerSet.has(r.user_id),
      }));

      const enrichedUnclaimed = unclaimedAuthors.map(a => ({
        id:               `unclaimed-${a.id}`,
        authors_table_id: a.id,
        user_id:          null,
        name:             a.display_name || a.email,
        handle:           null,
        email:            a.email,
        bio:              a.bio || '',
        avatar_url:       a.profile_photo_url || null,
        social_links:     [],
        is_verified:      false,
        status:           'active',
        books_count:      bookCountMap[a.id] || 0,
        readers_count:    readerCountMap[a.id] || 0,
        // Unclaimed authors have no rating computed for them, which is not the
        // same as a rating of zero. null so the stat renders a dash.
        avg_rating:       null,
        created_at:       a.created_at,
        hasPortalAccount: false,
      }));

      const all = [...enrichedClaimed, ...enrichedUnclaimed];
      setAuthors(all);
      setStats({
        total:     all.length,
        active:    enrichedClaimed.filter(a => a.status !== 'suspended').length,
        suspended: enrichedClaimed.filter(a => a.status === 'suspended').length,
      });
      // 'suspended', not 'authors': 'authors' is pending author books (loadMeta), and a
      // suspended author is not waiting work, so it must not overwrite it.
      onBadgeChange?.('suspended', enrichedClaimed.filter(a => a.status === 'suspended').length);
    } catch (err) {
      console.error('AdminAuthorSection load error:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadPortalMetrics(); }, [load, loadPortalMetrics]);

  function handleStatusChange(id, newStatus, newVerified) {
    setAuthors(prev => prev.map(a => a.id === id
      ? { ...a, status: newStatus ?? a.status, is_verified: newVerified ?? a.is_verified }
      : a
    ));
    setAuthors(prev => {
      setStats({
        total:     prev.length,
        active:    prev.filter(a => a.status !== 'suspended').length,
        suspended: prev.filter(a => a.status === 'suspended').length,
      });
      return prev;
    });
    if (selected?.id === id) setSelected(a => ({
      ...a, status: newStatus ?? a.status, is_verified: newVerified ?? a.is_verified,
    }));
  }

  const TABS = [
    { id: 'all',       label: 'All',              fn: () => true },
    { id: 'portal',    label: '✅ Portal Login',   fn: a => a.hasPortalAccount },
    { id: 'active',    label: 'Active',            fn: a => a.hasPortalAccount && a.status !== 'suspended' },
    { id: 'suspended', label: 'Suspended',         fn: a => a.status === 'suspended' },
    { id: 'unclaimed', label: '📋 Catalog Only',   fn: a => !a.hasPortalAccount },
    // The other side of the Users list's "Both" filter: authors who signed up
    // on the author portal and never made a reader profile.
    { id: 'author_only', label: '✍️ Author only',  fn: a => a.hasPortalAccount && !a.hasReaderAccount },
    { id: 'both',        label: '📖✍️ Both',        fn: a => a.hasPortalAccount && a.hasReaderAccount },
  ];

  const activeTab = TABS.find(t => t.id === filter) || TABS[0];
  const visible = authors.filter(a => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || (a.name   || '').toLowerCase().includes(q)
      || (a.handle || '').toLowerCase().includes(q)
      || (a.email  || '').toLowerCase().includes(q);
    return matchSearch && activeTab.fn(a);
  });

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 'var(--space-xxl)' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 'var(--space-none)' }}>Authors</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)', marginBottom: 'var(--space-none)' }}>{stats.total} authors on the platform</p>
      </div>

      <StatsBar {...stats} isMobile={isMobile} />
      <PortalMetricsBar metrics={portalMetrics} accountCount={stats.active} isMobile={isMobile} />

      <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', background: 'var(--brand-white)', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', padding: '8px 14px', flex: 1 }}>
          <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, handle or email…"
            style={{ border: 'none', outline: 'none', fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)', background: 'transparent', flex: 1, fontFamily: 'var(--font-body)' }} />
        </div>
        <button onClick={() => { load(); loadPortalMetrics(); }} style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--brand-sand)', background: 'var(--brand-white)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', color: 'var(--neutral-500)', fontFamily: 'var(--font-body)', flexShrink: 0 }}>↻</button>
      </div>

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '1.5px solid var(--brand-sand)', paddingTop: 'var(--space-xs)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {TABS.map(t => {
            const count = authors.filter(t.fn).length;
            const isActive = filter === t.id;
            return (
              <button key={t.id} onClick={() => setFilter(t.id)} style={{
                fontSize: isMobile ? 11.5 : 13, fontWeight: isActive ? 700 : 500,
                padding: isMobile ? '10px 12px' : '12px 18px',
                border: 'none', background: 'transparent',
                color: isActive ? 'var(--neutral-800)' : 'var(--neutral-400)',
                borderBottom: isActive ? '2px solid var(--neutral-800)' : '2px solid transparent',
                marginBottom: -1.5, cursor: 'pointer',
                fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {t.label}{count > 0 ? ` (${count})` : ''}
              </button>
            );
          })}
        </div>

        {!isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 180px', padding: '10px 24px', background: 'var(--brand-paper)', borderBottom: '1px solid var(--brand-sand)' }}>
            {['Author', 'Books', 'Portal', 'Status', 'Added', 'Actions'].map(h => (
              <span key={h} style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</span>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-circle)', border: '3px solid var(--brand-sand)', borderTopColor: 'var(--feedback-warning)', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Loading authors…</p>
          </div>
        ) : visible.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <p style={{ fontSize: 'var(--font-size-h2)', margin: '0 0 8px' }}>✍️</p>
            <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>
              {search ? `No authors matching "${search}"` : 'No authors in this category'}
            </p>
          </div>
        ) : visible.map((author, i) => (
          isMobile ? (
            <div key={author.id} style={{ padding: '14px 16px', borderBottom: i < visible.length - 1 ? '1px solid var(--brand-cream)' : 'none', background: author.status === 'suspended' ? '#fff8f8' : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', marginBottom: 'var(--space-10)' }}>
                <div style={{ width: 38, height: 38, borderRadius: 'var(--radius-circle)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0, border: '1px solid var(--brand-sand)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>
                  {author.avatar_url ? <img src={author.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '✍️'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)' }}>{author.name}</span>
                    <StatusPill status={author.status} verified={author.is_verified} />
                    {author.hasPortalAccount
                      ? <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: 'var(--feedback-success-bg)', color: '#16a34a' }}>✅ Portal</span>
                      : <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)' }}>📋 Catalog Only</span>
                    }
                    {author.hasReaderAccount && (
                      <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' }}>📖 Reader</span>
                    )}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', marginTop: 'var(--space-xxs)' }}>
                    {author.handle ? `@${author.handle}` : author.email || '—'}{' · '}{author.books_count ?? 0} books{' · '}{timeAgo(author.created_at)}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
                <button onClick={() => setSelected(author)} style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid var(--brand-sand)', background: 'var(--brand-white)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', color: 'var(--neutral-800)', fontFamily: 'var(--font-body)' }}>View</button>
                {author.hasPortalAccount && (
                  <button onClick={async () => {
                    const ns = await toggleAuthorSuspend(author);
                    if (ns) handleStatusChange(author.id, ns);
                  }} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 600, fontFamily: 'var(--font-body)', background: author.status === 'suspended' ? '#d1fae5' : 'var(--feedback-error-bg)', color: author.status === 'suspended' ? '#065f46' : 'var(--feedback-error)' }}>
                    {author.status === 'suspended' ? 'Restore' : 'Suspend'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div key={author.id} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 180px', padding: '14px 24px', alignItems: 'center', borderBottom: i < visible.length - 1 ? '1px solid var(--brand-cream)' : 'none', background: author.status === 'suspended' ? '#fff8f8' : 'transparent', transition: 'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background = author.status === 'suspended' ? '#fff0f0' : '#faf8f5'}
              onMouseLeave={e => e.currentTarget.style.background = author.status === 'suspended' ? '#fff8f8' : 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-circle)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0, border: '1px solid var(--brand-sand)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xl)' }}>
                  {author.avatar_url ? <img src={author.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '✍️'}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)' }}>{author.name}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>{author.handle ? `@${author.handle}` : author.email || '—'}</div>
                </div>
              </div>
              <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--neutral-800)' }}>{author.books_count ?? 0}</div>
              <div>
                {author.hasPortalAccount
                  ? <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '3px 8px', borderRadius: 'var(--radius-md)', background: 'var(--feedback-success-bg)', color: '#16a34a', border: '1px solid #bbf7d0' }}>✅ Active</span>
                  : <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '3px 8px', borderRadius: 'var(--radius-md)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' }}>📋 Catalog Only</span>
                }
              </div>
              {/* Fixed width so the status pill and the Reader pill are the
                  same size. Left to themselves each sized to its own text, so
                  a green "Active" sat noticeably wider than the blue "Reader"
                  stacked under it. textAlign is inherited by both. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', width: 104, textAlign: 'center' }}>
                <StatusPill status={author.status} verified={author.is_verified} />
                {author.hasReaderAccount && (
                  <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '4px 8px', borderRadius: 'var(--radius-xxl)', background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', whiteSpace: 'nowrap', boxSizing: 'border-box' }}>📖 Reader</span>
                )}
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>{timeAgo(author.created_at)}</div>
              <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
                <button onClick={() => setSelected(author)} style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid var(--brand-sand)', background: 'var(--brand-white)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', color: 'var(--neutral-800)', fontFamily: 'var(--font-body)' }}>View</button>
                {author.hasPortalAccount && (
                  <button onClick={async () => {
                    const ns = await toggleAuthorSuspend(author);
                    if (ns) handleStatusChange(author.id, ns);
                  }} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 600, fontFamily: 'var(--font-body)', background: author.status === 'suspended' ? '#d1fae5' : 'var(--feedback-error-bg)', color: author.status === 'suspended' ? '#065f46' : 'var(--feedback-error)' }}>
                    {author.status === 'suspended' ? 'Restore' : 'Suspend'}
                  </button>
                )}
              </div>
            </div>
          )
        ))}
      </div>

      {!loading && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', marginTop: 'var(--space-10)', textAlign: 'right' }}>
          Showing {visible.length} of {authors.length} authors
        </p>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {selected && (
        <AuthorDetailModal author={selected} onClose={() => setSelected(null)} onStatusChange={handleStatusChange} />
      )}
    </div>
  );
}